// Слой расчёта метрик сессии CV-упражнения — кабинет врача, MVP.
//
// Вход: тело отчёта (session.report из GET /api/cv-reports/{id}/trace) и
// покадровый trace (session.trace). Выход: готовая модель для экрана отчёта
// (SessionReport.jsx). Никакого React и запросов — чистые функции, их легко
// тестировать (sessionMetrics.test.js).
//
// Термины и правила — сводная спецификация «Метрики и графики» (редакция 2):
// - «Правильные повторы» — повтор, у которого выполнены все условия
//   (растянул губы, поднял уголки, удержал позу). В trace это feedback
//   'correct_hold'; fallback без trace — нет замечаний И поза удержана
//   (finished). Семантику category_name подтвердила врач (25.10): "5" —
//   повтор без замечаний, "2" — с замечаниями; учитываем в fallback'е.
// - Амплитуды — в % от опорного размера лица; по словам врача, 100% —
//   типичный размер лица (межзрачковое расстояние или похожий), точное
//   название эталона подтвердит CV-команда. Группы метрик и подписи
//   колонок сверены с демо-страницей /demo/full-metrics (25.10).
// - Качество записи — MVP-прокси: доля кадров, где лицо распознано (есть
//   числовые признаки). Флаги качества кадра — доработка CV (не блокирует).
// - Замечания — текстами, без кодов; «Начинайте» (команда старта) и
//   «У вас N секунд» (таймер) на кривой не помечаем.

// --- Служебные колонки trace (не признаки) ---
const SERVICE_COLUMNS = new Set(['frame', 't_ms', 'rep_id', 'hold_done_ms']);
// Порог — колонка, имя которой оканчивается на эти суффиксы (словарь Task 77).
const THRESHOLD_SUFFIX = /(_needed|_min|_max|_thresh|_threshold)$/;
// Порог для hold_done_ms называется hold_ms и на суффикс не похож.
const EXTRA_THRESHOLDS = new Set(['hold_ms']);

// Подсказки-не-замечания (FeedbackMapper.js тренажёра): их не рисуем на
// кривой и не считаем замечаниями повтора.
export const HINT_CODES = new Set(['31', '8']);
const START_HINT_RE = /^начина/i;      // «Начинайте» / «Начинай» (код 31)
const TIMER_HINT_RE = /^у вас \d+ секунд/i; // «У вас 10 секунд» (код 8)

// Тексты замечаний по кодам local_errors. Синхронизирован со словарём
// ERROR_TEXTS в FeedbackMapper.js тренажёра; незнакомый код — без выдумок.
const REMARK_TEXTS = {
  4: 'Растяни губы и улыбнись',
  5: 'Ты делаешь слишком быстро, держи подольше',
  6: 'Смотри в камеру, не отворачивайся',
  8: 'У вас 10 секунд',
  15: 'Сведи губы вместе, вытяни их в поцелуе',
  16: 'Не открывай рот так широко',
  20: 'Сомкни губы посильнее',
  21: 'Не открывай рот слишком широко',
  22: 'Опусти подбородок',
  60: 'Больше покажи зубы',
  61: 'Сомкни зубы',
  62: 'Не высовывай язык',
  63: 'Растяни губы пошире, чтобы были видны все зубы',
  64: 'Открой рот пошире',
  140: 'Не закрывай глаза, держи их открытыми',
  141: 'Подними обе брови, а не одну',
  142: 'Подними брови выше',
  201: 'Открой рот и округли губы',
  206: 'Подними уголки губ выше',
};

// Подписи колонок — как на демо-странице /demo/full-metrics (формулировки CV).
// Отступления от формулировок CV — по упражнению, см. COLUMN_LABEL_OVERRIDES.
const COLUMN_LABELS = {
  stretch: 'Растяжение губ',
  mouth_open: 'Открытие рта',
  teeth_lip_area_ratio: 'Видимость зубов',
  teeth_gap_ratio: 'Сжатие зубов',
  mouth_stretch: 'Рот не слишком широкий',
  smile_angle: 'Угол улыбки',
  mouth_width: 'Ширина рта',
  corner_height: 'Высота уголков губ',
  mouth_round: 'Высота рта к ширине',
  lips_gap: 'Щель между губами',
  brow: 'Брови',
  eye: 'Глаза открыты',
};
// Пороги, чьё имя не выводится из базы простым суффиксом.
const THRESHOLD_ALIASES = {
  eye: ['eyes_open_min'],
  brow: ['brows_needed'],
  // «Поцелуйчик»: lips_gap_max — губы сомкнуты, lips_gap_open_max — выше него «не открывай рот»
  lips_gap: ['lips_gap_open_max'],
};
// Подписи порогов, где «не больше» / «нужно» не различает два порога одной группы.
const THRESHOLD_LABELS = {
  lips_gap_max: 'сомкнуты ниже',
  lips_gap_open_max: 'открыт выше',
};
// Единицы оси: угол улыбки — градусы, удержание — миллисекунды, остальное —
// % от опорного размера лица.
const DEGREE_COLUMNS = new Set(['smile_angle']);
const MS_COLUMNS = new Set(['hold_done_ms', 'hold_ms']);

export const isFinished = (v) =>
  v === true || Number(v) === 1 || String(v).toLowerCase() === 'true';

// local_errors: [{ "206": "1" }, ...] — замечание там, где категория не "0".
export function hasRemarks(localErrors) {
  if (!Array.isArray(localErrors)) return false;
  for (const obj of localErrors) {
    for (const cat of Object.values(obj || {})) {
      if (String(cat) !== '0') return true;
    }
  }
  return false;
}

export function remarkCodes(localErrors) {
  if (!Array.isArray(localErrors)) return [];
  const codes = [];
  for (const obj of localErrors) {
    for (const [code, cat] of Object.entries(obj || {})) {
      if (String(cat) !== '0' && !codes.includes(String(code))) codes.push(String(code));
    }
  }
  return codes;
}

const remarkText = (code) => REMARK_TEXTS[code] || `Замечание (код ${code})`;

// --- Парсинг trace ---
// trace = { trace_version, columns: {имя: [значения по кадрам]}, frames, truncated }
// Тип колонки — по первому не-null: number → признак, boolean → проверка,
// string → feedback; null = разрыв (НЕ ноль).
export function parseTrace(trace) {
  if (!trace || typeof trace !== 'object' || !trace.columns) return null;
  const names = Object.keys(trace.columns);
  const rows = names.length ? trace.columns[names[0]].length : (trace.frames || 0);
  if (!rows) return null;
  const columns = {};
  for (const name of names) {
    const values = Array.isArray(trace.columns[name]) ? trace.columns[name] : [];
    let type = 'string';
    for (const v of values) {
      if (v === null || v === undefined) continue;
      type = typeof v === 'number' ? 'number' : typeof v === 'boolean' ? 'boolean' : 'string';
      break;
    }
    columns[name] = { values, type };
  }
  const tValues = columns.t_ms?.values || null;
  const tOf = (i) => (tValues && typeof tValues[i] === 'number' ? tValues[i] / 1000 : i * 0.05);
  return { columns, frames: rows, tOf, names };
}

const isThresholdName = (name) =>
  THRESHOLD_SUFFIX.test(name) || EXTRA_THRESHOLDS.has(name);

// Сигнал mouth_open у разных упражнений разный, поэтому подпись — по упражнению.
// show-teeth («заборчик»): сигнал — насколько разомкнулись губы (верхняя отошла
//   от нижней при растяжении улыбки); челюсть неподвижна, зубы сжаты, поэтому
//   «Открытие рта» вводило в заблуждение. Переименовано по решению врача
//   (29.10, сверено с видео).
// У остальных упражнений (улыбка и т.д.) это настоящее открывание рта —
//   подпись CV «Открытие рта» остаётся. Сверяем поупражненно по мере разбора.
const COLUMN_LABEL_OVERRIDES = {
  'show-teeth': { mouth_open: 'Размыкание губ' },
};

// Пара для блока «Амплитуда/Симметрия» — явный приоритет по упражнению
// (решение по итогам покадрового разбора trace, 29.10). Раньше пара выбиралась
// как «первая парная группа» — то есть случайно, по порядку колонок trace:
// у улыбки первой шла mouth_width_left/right, технические половины ширины.
// Их отношение — это положение опорной точки трекинга CV (точка систематически
// смещена и дрейфует при улыбке), а не симметрия: при симметричном повторе
// зазор половин до +13%, при кривом линии сплетаются.
// - show-teeth: пара «Растяжение губ» (stretch_left/right) — движение каждого
//   уголка от его же покоя, опорной точки в формуле нет. Симметрия корректна.
// - smiley-face: пара «Высота уголков губ» (corner_height_left/right) —
//   вертикальные подъёмы от покоя, тоже без опорной точки.
const SYM_PAIR_BY_EXERCISE = {
  'show-teeth': 'stretch',
  'smiley-face': 'corner_height',
  // kisses: половины округлости — высота губы на своей стороне к своей половине ширины
  kisses: 'mouth_round',
};

// Знакопеременные пары: значение может уходить ниже нуля (уголок ниже покоя —
// у улыбки правый уголок в большинстве повторов опускался до −2…−5%). Отношение
// пиков для них неинтерпретируемо (получались −275%…−1876%), формула симметрии
// согласовывается с CV-командой — число пока не показываем (symPending).
const SIGNED_PAIRS = new Set(['corner_height']);

// Линии, убранные с графиков по решению врача (29.10). Половины ширины рта
// улыбки — расстояния от опорной точки CV до уголков; их расхождение —
// положение точки трекинга, а не асимметрия. Кривизну улыбки показывает
// пара corner_height_left/right («Высота уголков губ»).
const HIDDEN_PAIR_LINES = {
  'smiley-face': new Set(['mouth_width_left', 'mouth_width_right']),
};

const humanizeBase = (key, exerciseSlug) =>
  (exerciseSlug && COLUMN_LABEL_OVERRIDES[exerciseSlug]?.[key]) ||
  COLUMN_LABELS[key] ||
  key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// --- Группы колонок для графика ---
// Пара: x_left/x_right (симметрия живёт в паре). Одиночка — самостоятельная
// линия. Порог группы: base_needed/min/max + алиасы (eyes_open_min и т.п.).
export function buildGroups(parsed, exerciseSlug) {
  if (!parsed) return [];
  const features = parsed.names.filter(
    (n) => parsed.columns[n].type === 'number' && !SERVICE_COLUMNS.has(n) && !isThresholdName(n),
  );
  const groups = [];
  const used = new Set();
  for (const name of features) {
    if (!name.endsWith('_left')) continue;
    const base = name.slice(0, -'_left'.length);
    const right = `${base}_right`;
    if (!features.includes(right)) continue;
    const lines = [
      { key: name, label: 'левый' },
      { key: right, label: 'правый' },
    ];
    // Базовая колонка (суммарная), если CV её присылает, — в ту же группу,
    // как на демо-странице (например, mouth_width + mouth_width_left/right).
    if (features.includes(base)) {
      lines.unshift({ key: base, label: '' });
      used.add(base);
    }
    groups.push({
      key: base,
      label: humanizeBase(base, exerciseSlug),
      units: DEGREE_COLUMNS.has(base) ? 'deg' : 'pct',
      isPair: true,
      lines,
      thresholds: thresholdKeys(base, parsed),
    });
    used.add(name);
    used.add(right);
  }
  for (const name of features) {
    if (used.has(name)) continue;
    const base = name;
    groups.push({
      key: base,
      label: humanizeBase(base, exerciseSlug),
      units: DEGREE_COLUMNS.has(base) ? 'deg' : 'pct',
      isPair: false,
      lines: [{ key: name, label: '' }],
      thresholds: thresholdKeys(base, parsed),
    });
  }
  // Пороги персонализации без своей метрики (open_lips_thresh и т.п.) —
  // отдельная группа, как на демо-странице.
  const consumed = new Set();
  groups.forEach((g) => g.thresholds.forEach((th) => consumed.add(th.key)));
  const orphan = parsed.names.filter(
    (n) => n !== 'hold_ms' && parsed.columns[n].type === 'number' && isThresholdName(n) && !consumed.has(n),
  );
  if (orphan.length) {
    groups.push({
      key: 'personalization',
      label: 'Пороги персонализации',
      units: 'pct',
      isPair: false,
      lines: [],
      thresholds: orphan.map((key) => ({
        key,
        value: parsed.columns[key].values.find((v) => typeof v === 'number') ?? null,
        label: 'порог',
      })),
    });
  }
  // Дорожка удержания: hold_done_ms против порога hold_ms (миллисекунды).
  if (parsed.columns.hold_done_ms?.type === 'number') {
    const hm = parsed.columns.hold_ms;
    groups.push({
      key: 'hold',
      label: 'Удержание',
      units: 'ms',
      isPair: false,
      lines: [{ key: 'hold_done_ms', label: 'удержано' }],
      thresholds:
        hm?.type === 'number'
          ? [{ key: 'hold_ms', value: hm.values.find((v) => typeof v === 'number') ?? null, label: 'нужно' }]
          : [],
    });
  }

  // Отступления по упражнению: убранные линии (см. HIDDEN_PAIR_LINES).
  // Если после удаления линий в группе осталось меньше двух — она больше не пара:
  // блок «Амплитуда/Симметрия» к ней не привязывается (у улыбки перейдёт к
  // corner_height — явному приоритету из SYM_PAIR_BY_EXERCISE).
  if (exerciseSlug && HIDDEN_PAIR_LINES[exerciseSlug]) {
    const hidden = HIDDEN_PAIR_LINES[exerciseSlug];
    for (const g of groups) {
      const kept = g.lines.filter((l) => !hidden.has(l.key));
      if (kept.length !== g.lines.length) {
        g.lines = kept;
        if (kept.length < 2) g.isPair = false;
      }
    }
  }
  return groups;
}

function thresholdKeys(base, parsed) {
  const candidates = [`${base}_needed`, `${base}_min`, `${base}_max`, ...(THRESHOLD_ALIASES[base] || [])];
  const kindOf = (n) => (n.endsWith('_max') ? 'max' : 'min');
  const out = [];
  for (const key of candidates) {
    const col = parsed.columns[key];
    if (!col || col.type !== 'number') continue;
    const v = col.values.find((x) => x !== null && x !== undefined);
    if (typeof v !== 'number') continue;
    out.push({ key, value: v, label: THRESHOLD_LABELS[key] || (kindOf(key) === 'max' ? `не больше` : `нужно`) });
  }
  return out;
}

// Группа для блока «Амплитуда/Симметрия»: явный приоритет по упражнению
// (SYM_PAIR_BY_EXERCISE), иначе первая парная. exerciseSlug может отсутствовать
// (старые вызовы) — тогда поведение прежнее, по порядку колонок.
export const defaultGroup = (groups, exerciseSlug) => {
  const want = exerciseSlug ? SYM_PAIR_BY_EXERCISE[exerciseSlug] : null;
  return (
    (want && groups.find((g) => g.key === want && g.isPair)) ||
    groups.find((g) => g.isPair) ||
    groups[0] ||
    null
  );
};

// --- Границы повторов по rep_id trace ---
// known: повтор есть в info_reps отчёта (у лишнего частичного репа — false).
export function repBounds(parsed) {
  if (!parsed || !parsed.columns.rep_id) return [];
  const { values } = parsed.columns.rep_id;
  const map = new Map();
  for (let i = 0; i < values.length; i += 1) {
    const id = values[i];
    if (id === null || id === undefined) continue;
    if (!map.has(id)) map.set(id, { id, i0: i, i1: i });
    else map.get(id).i1 = i;
  }
  return [...map.values()].map(({ id, i0, i1 }) => ({
    id,
    t0: parsed.tOf(i0),
    t1: parsed.tOf(i1),
  }));
}

// Полосы кадров без распознанного лица: все признаки разом null.
function nullBands(parsed, featureNames) {
  const bands = [];
  let start = -1;
  for (let i = 0; i < parsed.frames; i += 1) {
    let valid = false;
    for (const name of featureNames) {
      const v = parsed.columns[name].values[i];
      if (v !== null && v !== undefined) { valid = true; break; }
    }
    if (!valid && start < 0) start = i;
    if (valid && start >= 0) {
      bands.push([parsed.tOf(start), parsed.tOf(i - 1)]);
      start = -1;
    }
  }
  if (start >= 0) bands.push([parsed.tOf(start), parsed.tOf(parsed.frames - 1)]);
  return bands.map(([t0, t1]) => ({ t0, t1 })).filter((b) => b.t1 - b.t0 > 0.08);
}

function qualityRatio(parsed, featureNames) {
  if (!parsed.frames || featureNames.length === 0) return null;
  let valid = 0;
  for (let i = 0; i < parsed.frames; i += 1) {
    for (const name of featureNames) {
      const v = parsed.columns[name].values[i];
      if (v !== null && v !== undefined) { valid += 1; break; }
    }
  }
  return valid / parsed.frames;
}

// --- Медиана (амплитуда и симметрия — медианы по повторам) ---
export function median(arr) {
  const xs = arr.filter((x) => typeof x === 'number' && Number.isFinite(x)).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

export const round1 = (v) => Math.round(v * 10) / 10;
export const roundPct = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : null);

// --- Пики признака по повторам (для амплитуд и симметрии) ---
function peakByRep(parsed, colKey, reps) {
  const col = parsed.columns[colKey];
  if (!col) return new Map();
  const { values } = col;
  const repCol = parsed.columns.rep_id?.values || [];
  const peaks = new Map();
  for (const rep of reps) {
    let peak = null;
    for (let i = 0; i < values.length; i += 1) {
      if (repCol[i] !== rep.repId) continue;
      const v = values[i];
      if (typeof v === 'number' && (peak === null || v > peak)) peak = v;
    }
    peaks.set(rep.repId, peak);
  }
  return peaks;
}

// Минимум кадров в повторе, при котором симметрия вообще имеет смысл.
// Фантомный повтор (1–2 кадра в паузе между движениями, рот ещё закрыт) даёт
// «шум ÷ шум» — выбросы вида 549%/757% (разобрано с врачом 28.10 на записи
// заборчика 24.10 09:59: у «повтора 3» в trace был ровно 1 кадр).
export const MIN_FRAMES_FOR_SYM = 10;

// Число кадров trace, отнесённых к каждому rep_id (для отсева фантомных повторов).
export function frameCountByRep(parsed) {
  const counts = new Map();
  const repCol = parsed?.columns.rep_id?.values || [];
  for (const id of repCol) {
    if (id === null || id === undefined) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  return counts;
}

// Нормализованные повторы из отчёта: CV-сервер кладёт их в exercise.info_reps,
// старые браузерные отчёты — в report.reps (плоские объекты).
function normalizeReps(report) {
  const exercise = report?.exercise || {};
  const raw = Array.isArray(exercise.info_reps)
    ? exercise.info_reps
    : Array.isArray(report?.reps)
      ? report.reps
      : [];
  return raw.map((r, idx) => {
    const timers = {};
    for (const [k, v] of Object.entries(r || {})) {
      if (/^timer_\d+s(_seconds)?$/.test(k) || /^percent_timer_\d+s$/.test(k)) timers[k] = v;
    }
    return {
      repId: Number.isFinite(Number(r?.rep_id)) ? Number(r.rep_id) : idx + 1,
      finished: isFinished(r?.finished),
      localErrors: Array.isArray(r?.local_errors) ? r.local_errors : [],
      codes: remarkCodes(r?.local_errors),
      // Категория качества повтора от CV: "5" — без замечаний, "2" — с
      // замечаниями (подтверждено врачом 25.10; уточнение — у CV-команды).
      category: r?.category_name != null && r?.category_name !== '' ? String(r.category_name) : null,
      timers,
      systemTime: typeof r?.system_time === 'number' ? r.system_time : null,
    };
  });
}

// Удержание из таймеров отчёта: timer_10s → «окно 10 с», timer_3s → «поза 3 с».
// Считаем долю успешных среди повторов, где таймер вообще отмечен.
function holdFromTimers(reps) {
  const parts = [];
  const timerKeys = [...new Set(reps.flatMap((r) => Object.keys(r.timers).filter((k) => /^timer_\d+s$/.test(k))))];
  const secOf = (k) => Number(k.match(/^timer_(\d+)s$/)?.[1] || 0);
  timerKeys
    .sort((a, b) => secOf(b) - secOf(a))
    .forEach((k) => {
      const marked = reps.filter((r) => k in r.timers);
      if (!marked.length) return;
      const ok = marked.filter((r) => isFinished(r.timers[k]) || r.timers[k] === 1).length;
      const label = `${secOf(k)} с`;
      const share = Math.round((ok / marked.length) * 100);
      parts.push(ok === marked.length ? `${label} — ${share}% (${ok} повт.)` : `${label} — ${share}% (${ok} из ${marked.length} повт.)`);
    });
  return parts.join('; ');
}

const ABORT_TEXTS = {
  few_repetitions: 'мало повторов — окно закончилось раньше',
  time_limit: 'закончилось время упражнения',
};

// --- Главная функция: модель сессии для экрана врача ---
// report — тело отчёта (session.report), trace — покадровая запись или null.
export function buildSessionModel(report, trace, { createdAt, exerciseSlug } = {}) {
  const exercise = report?.exercise || {};
  const reps = normalizeReps(report);
  const totalReps = reps.length || Number(exercise.final_result?.count_replays) || 0;

  // Качество и геометрия кривой — только с trace.
  const parsed = parseTrace(trace);
  const groups = buildGroups(parsed, exerciseSlug);
  const featureNames = parsed
    ? parsed.names.filter((n) => parsed.columns[n].type === 'number' && !SERVICE_COLUMNS.has(n) && !isThresholdName(n))
    : [];
  const qualityRatioVal = parsed ? qualityRatio(parsed, featureNames) : null;
  const qualityPct = qualityRatioVal === null ? null : Math.round(qualityRatioVal * 100);

  // Правильные повторы: сначала по trace ('correct_hold'), иначе по отчёту.
  let correctCount = null;
  let correctSource = null;
  if (parsed && parsed.columns.feedback) {
    const fb = parsed.columns.feedback.values;
    const repCol = parsed.columns.rep_id?.values || [];
    const ok = new Set();
    for (let i = 0; i < fb.length; i += 1) {
      if (fb[i] === 'correct_hold' && repCol[i] !== null && repCol[i] !== undefined) ok.add(repCol[i]);
    }
    correctCount = ok.size;
    correctSource = 'trace';
  }
  if (correctCount === null && reps.length) {
    // Правило по отчёту: замечаний нет (код или категория "2") И поза удержана.
    correctCount = reps.filter((r) => !r.codes.length && r.category !== '2' && r.finished).length;
    correctSource = 'report';
  }
  if (correctCount === null) correctCount = 0;

  const remarksReps = reps.filter((r) => r.codes.length || r.category === '2');
  const remarksPct = totalReps ? Math.round((remarksReps.length / totalReps) * 100) : 0;

  // Замечания по типам (для «Детализации») — все коды info_reps, без «Начинайте».
  const counts = new Map();
  reps.forEach((r) => r.codes.forEach((code) => counts.set(code, (counts.get(code) || 0) + 1)));
  const remarksBars = [...counts.entries()]
    .map(([code, n]) => ({ code, text: remarkText(code), n }))
    .sort((a, b) => b.n - a.n || a.text.localeCompare(b.text));

  const abortKey = exercise.abort_exercise || null;

  // Удержание: таймеры отчёта + фактическое из trace (hold_done_ms/hold_ms).
  const holdText = holdFromTimers(reps);
  let holdFact = null;
  if (parsed && parsed.columns.hold_done_ms) {
    const hd = parsed.columns.hold_done_ms.values;
    const repCol = parsed.columns.rep_id?.values || [];
    const perRep = new Map();
    for (let i = 0; i < hd.length; i += 1) {
      if (typeof hd[i] !== 'number') continue;
      const id = repCol[i];
      const cur = perRep.get(id) || 0;
      if (hd[i] > cur) perRep.set(id, hd[i]);
    }
    const best = Math.max(0, ...perRep.values());
    const holdMsCol = parsed.columns.hold_ms?.values?.find((v) => typeof v === 'number');
    holdFact = { sec: round1(best / 1000), targetSec: holdMsCol ? round1(holdMsCol / 1000) : null };
  }

  // Метки на кривой: подсказки-замечания из trace (тексты) или из отчёта
  // (realtime_feedbacks без кодов 31/8). «Начинайте» и «У вас N секунд» —
  // не замечания, по решению врача на кривой их нет.
  let flags = [];
  if (parsed && parsed.columns.feedback) {
    const fb = parsed.columns.feedback.values;
    let last = null;
    flags = [];
    for (let i = 0; i < fb.length; i += 1) {
      const v = fb[i];
      if (typeof v !== 'string' || !v || v === 'correct_hold') { last = v ?? last; continue; }
      if (v === last) continue;
      last = v;
      if (START_HINT_RE.test(v) || TIMER_HINT_RE.test(v)) continue;
      flags.push({ t: parsed.tOf(i), text: v });
    }
  } else if (Array.isArray(report?.realtime_feedbacks)) {
    flags = report.realtime_feedbacks
      .map((f) => ({
        t: typeof f.system_time === 'number' ? f.system_time : null,
        codes: Object.keys((f.local_errors || [{}])[0] || {}),
      }))
      .filter((f) => f.t !== null && f.codes.length && !f.codes.every((c) => HINT_CODES.has(String(c))))
      .map((f) => ({ t: f.t, text: remarkText(f.codes[0]) }));
  }
  flags.sort((a, b) => a.t - b.t);

  // Амплитуды и симметрия — по первой парной группе (медианы пиков повторов).
  // Симметрия считается только по повторам с достаточным числом кадров в trace
  // (MIN_FRAMES_FOR_SYM): иначе это отношение шума к шуму в паузе между
  // движениями. Такие повторы остаются в списке с пометкой «мало кадров».
  let amplitude = null;
  const pairGroup = defaultGroup(groups, exerciseSlug);
  const framesOfRep = frameCountByRep(parsed);
  if (parsed && pairGroup?.isPair && reps.length) {
    const knownIds = new Set(reps.map((r) => r.repId));
    const leftKey = (pairGroup.lines.find((l) => l.key.endsWith('_left')) || pairGroup.lines[0]).key;
    const rightKey = (pairGroup.lines.find((l) => l.key.endsWith('_right')) || pairGroup.lines[pairGroup.lines.length - 1]).key;
    const peaksL = peakByRep(parsed, leftKey, reps);
    const peaksR = peakByRep(parsed, rightKey, reps);
    const perRep = [];
    reps.forEach((r) => {
      const pl = peaksL.get(r.repId);
      const pr = peaksR.get(r.repId);
      if (pl === null || pr === null) return;
      const scale = pairGroup.units === 'deg' ? 1 : 100;
      const frames = framesOfRep.get(r.repId) || 0;
      const tooFew = frames < MIN_FRAMES_FOR_SYM;
      const sym = !tooFew && pr > 0 ? Math.round((pl / pr) * 100) : null;
      perRep.push({
        repId: r.repId,
        known: knownIds.has(r.repId),
        left: roundPct(pl * scale),
        right: roundPct(pr * scale),
        sym,
        // Причина пропуска — для пояснения на экране: «мало кадров (1)».
        symSkip: sym === null && tooFew ? `мало кадров (${frames})` : null,
        frames,
      });
    });
    const done = perRep.filter((p) => p.known);
    const symValues = done.map((p) => p.sym).filter((s) => s !== null);
    // Медианы амплитуд — по тем же «полноценным» повторам, что и симметрия
    // (кадров >= MIN_FRAMES_FOR_SYM): пик фантома — это хвост предыдущего
    // движения и шум (на заборчике 24.10 правый уголок: 23.3% с фантомом
    // против 30.9% без — медиана проседала).
    const doneSolid = done.filter((p) => p.frames >= MIN_FRAMES_FOR_SYM);
    amplitude = {
      groupKey: pairGroup.key,
      groupLabel: pairGroup.label,
      leftPct: roundPct(median(doneSolid.map((p) => p.left))),
      rightPct: roundPct(median(doneSolid.map((p) => p.right))),
      symValues,
      symMedianPct: median(symValues),
      weakerSide: symValues.length ? (median(symValues) < 100 ? 'левая' : 'правая') : null,
      perRep,
    };
    // Знакопеременная пара (см. SIGNED_PAIRS): отношение пиков не показываем —
    // формула симметрии для таких величин согласовывается с CV-командой (29.10).
    // Амплитуды Л/П (пики) остаются: «минус» = уголок не поднимался выше покоя,
    // это честная клиника. Повторы остаются в списке с пояснением symSkip.
    if (SIGNED_PAIRS.has(pairGroup.key)) {
      amplitude.symPending = true;
      amplitude.symValues = [];
      amplitude.symMedianPct = null;
      amplitude.weakerSide = null;
      amplitude.perRep = perRep.map((p) => ({ ...p, sym: null, symSkip: 'методика уточняется' }));
    }
  }

  // Сводка качества → заключение (бейдж). Без trace — «нет данных».
  const quality = { pct: qualityPct, label: qualityPct === null ? null : qualityPct >= 95 ? 'Надёжно' : qualityPct >= 80 ? 'Приемлемо' : 'Слабо' };
  const badge = qualityPct === null ? 'Нет записи' : qualityPct >= 95 ? 'Пригодна' : qualityPct >= 80 ? 'Сомнительна' : 'Негодна';

  // Ряды для графика: t (сек) + значения признаков (pct — ×100, мс — как есть).
  // Прореживание — огибающая min/max по пачкам кадров (как на демо-странице):
  // пики не теряются даже на длинных сессиях.
  let series = null;
  if (parsed) {
    const step = Math.max(1, Math.ceil(parsed.frames / 1000));
    const scaleOf = (unitKey) => (DEGREE_COLUMNS.has(unitKey) || MS_COLUMNS.has(unitKey) ? 1 : 100);
    const valueOf = (name, i) => {
      const v = parsed.columns[name]?.values[i];
      if (v === null || v === undefined) return null;
      const unitKey = name.replace(/_(left|right)$/, '').replace(/_(needed|min|max|thresh|threshold)$/, '');
      return scaleOf(unitKey) === 1 ? v : v * 100;
    };
    const t = [];
    const cols = {};
    featureNames.forEach((n) => { cols[n] = []; });
    // Удержание — в сервисных колонках (не признак), для графика добавляем отдельно.
    if (parsed.columns.hold_done_ms?.type === 'number') cols.hold_done_ms = [];
    const thresholds = {};
    parsed.names.forEach((n) => {
      if (parsed.columns[n].type === 'number' && isThresholdName(n)) thresholds[n] = [];
    });
    const names = [...Object.keys(cols), ...Object.keys(thresholds)];
    const push = (ti, pick) => {
      t.push(ti);
      names.forEach((n) => (cols[n] || thresholds[n]).push(pick(n)));
    };
    for (let l = 0; l < parsed.frames; l += step) {
      const r = Math.min(l + step, parsed.frames);
      if (step === 1) {
        push(parsed.tOf(l), (n) => valueOf(n, l));
        continue;
      }
      const mn = {};
      const mx = {};
      for (let i = l; i < r; i += 1) {
        names.forEach((n) => {
          const v = valueOf(n, i);
          if (v === null) return;
          if (mn[n] === undefined || v < mn[n]) mn[n] = v;
          if (mx[n] === undefined || v > mx[n]) mx[n] = v;
        });
      }
      push(parsed.tOf(l), (n) => (mn[n] === undefined ? null : mn[n]));
      if (r - 1 > l) push(parsed.tOf(r - 1), (n) => (mx[n] === undefined ? null : mx[n]));
    }
    series = { step, t, features: cols, thresholds, tMax: t[t.length - 1] ?? 0 };
  }

  return {
    createdAt: createdAt || null,
    exerciseSlug: exerciseSlug || null,
    hasTrace: !!parsed,
    truncated: !!trace?.truncated,
    frames: { total: parsed?.frames || 0, validPct: qualityPct },
    quality,
    badge,
    badgeReason: qualityPct === null
      ? 'покадровая запись недоступна — качество не оценить'
      : `${quality.pct}% кадров с распознанным лицом`,
    abort: abortKey ? { key: abortKey, text: ABORT_TEXTS[abortKey] || abortKey } : null,
    calibration: report?.calibration
      ? { measured: report.calibration.measured === true, id: report.calibration.id || report.calibration_data?.id || null }
      : null,
    thresholdsUpdated: report?.updated_settings || {},
    reps,
    totalReps,
    correct: { count: correctCount, total: totalReps, source: correctSource },
    withRemarks: { count: remarksReps.length, pct: remarksPct },
    remarksBars,
    hold: { text: holdText, fact: holdFact },
    groups,
    series,
    repBounds: parsed
      ? repBounds(parsed).map((b) => ({
          ...b,
          known: reps.some((r) => r.repId === b.id),
          frames: framesOfRep.get(b.id) || 0,
        }))
      : [],
    gaps: parsed ? nullBands(parsed, featureNames) : [],
    flags,
    amplitude,
    sessionId: report?.session_id || null,
    finalResult: exercise.final_result || null,
  };
}

// --- Сравнение с прошлой сессией («было → стало») ---
// status: better | worse | none (серая: сравнение некорректно — обрыв/нет данных).
export function buildComparison(cur, prev) {
  // Нет прошлой сессии — сравнивать нечего (UI покажет «первая сессия»).
  if (!prev || !cur) return [];
  const rows = [];
  const fmtInt = (v) => (v === null || v === undefined ? '—' : String(Math.round(v)));
  const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);
  const repStr = (m) => (m ? `${fmtInt(m.correct.count)} из ${fmtInt(m.correct.total)}` : '—');

  const sign = (d) => (d > 0 ? `▲ +${d}` : d < 0 ? `▼ −${Math.abs(d)}` : 'без изменений');
  const notComparable = !cur || !prev || !!cur.abort || !!prev.abort
    || cur.correct.total === 0 || prev.correct.total === 0;

  rows.push({
    name: 'Правильные повторы',
    was: repStr(prev),
    now: repStr(cur),
    delta: notComparable ? 'не сравнивать' : sign(cur.correct.count - prev.correct.count),
    status: notComparable ? 'none' : cur.correct.count === prev.correct.count ? 'none' : cur.correct.count > prev.correct.count ? 'better' : 'worse',
  });

  const amp = (m) => m?.amplitude;
  if (amp(cur) || amp(prev)) {
    ['leftPct', 'rightPct'].forEach((side, idx) => {
      const a = amp(cur)?.[side] ?? null;
      const b = amp(prev)?.[side] ?? null;
      const label = idx === 0 ? 'Амплитуда — левая (пик)' : 'Амплитуда — правая (пик)';
      if (a === null && b === null) {
        rows.push({ name: label, was: '—', now: '—', delta: '—', status: 'none' });
        return;
      }
      const d = a !== null && b !== null ? Math.round(a - b) : null;
      rows.push({
        name: label,
        was: pct(b),
        now: pct(a),
        delta: d === null ? 'нет данных' : d === 0 ? 'без изменений' : d > 0 ? `▲ +${d} п.п.` : `▼ −${Math.abs(d)} п.п.`,
        status: d === null || d === 0 ? 'none' : d > 0 ? 'better' : 'worse',
      });
    });
    const sa = amp(cur)?.symMedianPct ?? null;
    const sb = amp(prev)?.symMedianPct ?? null;
    if (sa !== null && sb !== null) {
      const d = Math.round(sa - sb);
      // Симметрия ближе к 100% — лучше.
      rows.push({
        name: 'Симметрия',
        was: pct(sb),
        now: pct(sa),
        delta: d === 0 ? 'без изменений' : `${d > 0 ? '▲ +' : '▼ −'}${Math.abs(d)} п.п.`,
        status: d === 0 ? 'none' : Math.abs(100 - sa) < Math.abs(100 - sb) ? 'better' : 'worse',
      });
    }
  }
  return rows;
}

// --- Тренд по занятиям упражнения (метрика — правильные повторы) ---
// entries: [{ createdAt, model }] любого порядка; сортируем по времени.
export function buildTrend(entries) {
  const items = entries
    .filter((e) => e?.model)
    .map((e) => ({
      date: e.createdAt ? new Date(e.createdAt) : null,
      createdAt: e.createdAt || null,
      correct: e.model.correct?.count ?? 0,
      total: e.model.correct?.total ?? 0,
      aborted: !!e.model.abort,
      abortText: e.model.abort?.text || null,
      thresholdsChanged: !!(e.model.thresholdsUpdated && Object.keys(e.model.thresholdsUpdated).length),
    }))
    .filter((e) => e.date && !Number.isNaN(e.date.getTime()))
    .sort((a, b) => a.date - b.date);
  return items.map((e, i) => ({
    ...e,
    label: e.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', ''),
    isLast: i === items.length - 1,
  }));
}
